/** Small synthesized sound kit: rolling, grinding, pops, landings, hits. No audio assets. */
export class ArcadeAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private rollGain: GainNode | null = null;
  private rollFilter: BiquadFilterNode | null = null;
  private grindGain: GainNode | null = null;
  private grindTone: OscillatorNode | null = null;
  private windGain: GainNode | null = null;
  private muted = false;

  public get isMuted(): boolean {
    return this.muted;
  }

  /** Must be called from a user gesture. */
  public start(): void {
    if (this.context) {
      void this.context.resume();
      return;
    }
    const Context =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return;
    const context = new Context();
    this.context = context;
    this.master = context.createGain();
    this.master.gain.value = this.muted ? 0 : 0.55;
    const compressor = context.createDynamicsCompressor();
    this.master.connect(compressor).connect(context.destination);

    const length = context.sampleRate * 2;
    this.noise = context.createBuffer(1, length, context.sampleRate);
    const data = this.noise.getChannelData(0);
    let brown = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      brown = (brown + 0.02 * white) / 1.02;
      data[i] = white * 0.6 + brown * 3;
    }

    const roll = this.loop();
    this.rollFilter = context.createBiquadFilter();
    this.rollFilter.type = 'bandpass';
    this.rollFilter.Q.value = 0.8;
    this.rollGain = context.createGain();
    this.rollGain.gain.value = 0;
    roll.connect(this.rollFilter).connect(this.rollGain).connect(this.master);

    const grind = this.loop();
    const grindFilter = context.createBiquadFilter();
    grindFilter.type = 'bandpass';
    grindFilter.frequency.value = 2600;
    grindFilter.Q.value = 2.5;
    this.grindGain = context.createGain();
    this.grindGain.gain.value = 0;
    grind.connect(grindFilter).connect(this.grindGain).connect(this.master);
    this.grindTone = context.createOscillator();
    this.grindTone.type = 'sawtooth';
    this.grindTone.frequency.value = 520;
    const toneGain = context.createGain();
    toneGain.gain.value = 0.08;
    this.grindTone.connect(toneGain).connect(this.grindGain);
    this.grindTone.start();

    const wind = this.loop();
    const windFilter = context.createBiquadFilter();
    windFilter.type = 'lowpass';
    windFilter.frequency.value = 500;
    this.windGain = context.createGain();
    this.windGain.gain.value = 0;
    wind.connect(windFilter).connect(this.windGain).connect(this.master);
  }

  private loop(): AudioBufferSourceNode {
    const source = this.context!.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;
    source.playbackRate.value = 0.8 + Math.random() * 0.4;
    source.start(0, Math.random() * 1.5);
    return source;
  }

  public toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master && this.context)
      this.master.gain.setTargetAtTime(this.muted ? 0 : 0.55, this.context.currentTime, 0.05);
    return this.muted;
  }

  /** Continuous layers driven by the local rider each frame. */
  public drive(speed: number, grounded: boolean, grinding: boolean, airborne: boolean): void {
    const context = this.context;
    if (!context || !this.rollGain || !this.rollFilter || !this.grindGain || !this.windGain) return;
    const now = context.currentTime;
    const rolling = grounded && speed > 0.5 ? Math.min(0.5, speed * 0.03) : 0;
    this.rollGain.gain.setTargetAtTime(rolling, now, 0.05);
    this.rollFilter.frequency.setTargetAtTime(180 + speed * 45, now, 0.1);
    this.grindGain.gain.setTargetAtTime(grinding ? 0.28 : 0, now, 0.03);
    this.grindTone?.frequency.setTargetAtTime(420 + speed * 22, now, 0.1);
    this.windGain.gain.setTargetAtTime(
      airborne || speed > 14 ? Math.min(0.25, speed * 0.012) : 0,
      now,
      0.2,
    );
  }

  private burst(options: {
    duration: number;
    frequency: number;
    type: BiquadFilterType;
    gain: number;
    q?: number;
  }): void {
    const context = this.context;
    if (!context || !this.master || !this.noise) return;
    const now = context.currentTime;
    const source = context.createBufferSource();
    source.buffer = this.noise;
    const filter = context.createBiquadFilter();
    filter.type = options.type;
    filter.frequency.value = options.frequency;
    filter.Q.value = options.q ?? 1;
    const gain = context.createGain();
    gain.gain.setValueAtTime(options.gain, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + options.duration);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(now, Math.random());
    source.stop(now + options.duration + 0.05);
  }

  private thump(frequency: number, duration: number, gain: number): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;
    const osc = context.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, frequency * 0.35), now + duration);
    const g = context.createGain();
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(g).connect(this.master);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  }

  public ollie(): void {
    this.burst({ duration: 0.06, frequency: 3200, type: 'highpass', gain: 0.6 });
    this.thump(160, 0.08, 0.5);
  }

  public land(impact: number): void {
    const strength = Math.min(1, 0.3 + impact / 10);
    this.thump(110, 0.18, 0.7 * strength);
    this.burst({ duration: 0.12, frequency: 900, type: 'lowpass', gain: 0.5 * strength });
  }

  public grindStart(): void {
    this.burst({ duration: 0.08, frequency: 4000, type: 'bandpass', gain: 0.4, q: 3 });
  }

  public whoosh(): void {
    const context = this.context;
    if (!context || !this.master || !this.noise) return;
    const now = context.currentTime;
    const source = context.createBufferSource();
    source.buffer = this.noise;
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.5;
    filter.frequency.setValueAtTime(500, now);
    filter.frequency.exponentialRampToValueAtTime(2400, now + 0.15);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(now, Math.random());
    source.stop(now + 0.2);
  }

  public impact(heavy: boolean): void {
    this.thump(heavy ? 90 : 130, heavy ? 0.3 : 0.18, heavy ? 1 : 0.75);
    this.burst({
      duration: heavy ? 0.2 : 0.1,
      frequency: 1800,
      type: 'bandpass',
      gain: heavy ? 0.9 : 0.6,
      q: 0.7,
    });
  }

  public bail(): void {
    this.burst({ duration: 0.45, frequency: 700, type: 'lowpass', gain: 0.8 });
    this.thump(70, 0.35, 0.8);
  }

  public knockout(): void {
    this.impact(true);
    this.thump(55, 0.6, 0.9);
  }

  public coins(): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;
    [880, 1320].forEach((frequency, index) => {
      const osc = context.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = frequency;
      const g = context.createGain();
      g.gain.setValueAtTime(0.0001, now + index * 0.08);
      g.gain.exponentialRampToValueAtTime(0.25, now + index * 0.08 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, now + index * 0.08 + 0.25);
      osc.connect(g).connect(this.master!);
      osc.start(now + index * 0.08);
      osc.stop(now + index * 0.08 + 0.3);
    });
  }

  public dispose(): void {
    void this.context?.close();
    this.context = null;
  }
}
